import React, { useState } from 'react'
import { Download, X } from 'lucide-react'
import { FieldWrapper } from '../FieldWrapper/FieldWrapper'
import { FileUpload } from '../FileUpload/FileUpload'
import { API_URL } from '../../../config/axios'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

interface Props {
  existingAttachments: string[]
  onRemoveExistingAttachment: (index: number) => void
  files: File[]
  onFilesChange: (files: File[]) => void
  readonly?: boolean
}

export const AttachmentsSection: React.FC<Props> = ({
  existingAttachments,
  onRemoveExistingAttachment,
  files,
  onFilesChange,
  readonly = false,
}) => {
  const [sizeError, setSizeError] = useState<string | null>(null)

  const handleFilesChange = (incoming: File[]) => {
    const oversized = incoming.filter((f) => f.size > MAX_FILE_SIZE_BYTES)
    const accepted = incoming.filter((f) => f.size <= MAX_FILE_SIZE_BYTES)

    if (oversized.length > 0) {
      setSizeError(
        oversized.length === 1
          ? `"${oversized[0].name}" ist zu groß (max. 10 MB).`
          : `${oversized.length} Dateien überschreiten 10 MB und wurden ignoriert.`
      )
    } else {
      setSizeError(null)
    }

    onFilesChange(accepted)
  }

  return (
    <div className="mt-4 max-[1535px]:mt-2">
      <FieldWrapper label="Anhänge">
        {existingAttachments.length > 0 && (
          <ul className="flex flex-col gap-2 mb-3">
            {existingAttachments.map((url, i) => {
              const name = url.split('/').pop() ?? url
              return (
                <li
                  key={url}
                  className="min-h-[44px] rounded-[18px] bg-[#EEF1F7] px-3 py-2 flex items-center gap-3"
                >
                  <a
                    href={`${API_URL}${url}`}
                    download
                    className="font-['Roboto'] text-[14px] text-[#124093] hover:underline truncate flex-1"
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {name}
                  </a>
                  <a
                    href={`${API_URL}${url}`}
                    download
                    className="h-8 w-8 shrink-0 flex items-center justify-center text-slate-400 hover:text-[#124093]"
                    aria-label={`Anhang ${name} herunterladen`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download size={16} />
                  </a>
                  {!readonly && (
                    <button
                      type="button"
                      onClick={() => onRemoveExistingAttachment(i)}
                      className="h-8 w-8 shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500"
                      aria-label={`Anhang ${name} entfernen`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {!readonly && <FileUpload value={files} onChange={handleFilesChange} />}
        {sizeError && (
          <p className="mt-1 text-xs text-red-500">{sizeError}</p>
        )}
      </FieldWrapper>
    </div>
  )
}
